import { createStyles, makeStyles } from "@material-ui/core";
import * as React from "react";

import {
    currencyFormatter,
    Label,
    Layout,
    RelodeTheme,
    Text,
} from "../../";
import { ReviewSummaryData } from "./reviewSummaryData";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {},
            salaryLayout: {
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(75px, 175px))",
                gridColumnGap: theme.spacing(2),
                gridRowGap: theme.spacing(2),
            },
            preserve: {
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
            },
            header: {
                marginBottom: theme.spacing(2),
            },
            content: {
                marginBottom: theme.spacing(5),
            },
            contentBodySection: {
                marginBottom: theme.spacing(2),
            },
        }),
    { name: "ReviewSummary" }
);

interface Props {
    data: ReviewSummaryData;
    showRelocation: boolean;
}

export const ReviewSummary = (props: Props) => {
    const classes = useStyles({});
    const { data, showRelocation } = props;
    const baseId = "reviewSummary";

    const formattedMin = currencyFormatter.format(
        data?.desiredSalaryMinInUSDollars
    );
    const formattedMax = currencyFormatter.format(
        data?.desiredSalaryMaxInUSDollars
    );

    if (!data) return null;

    return (
        <>
            <Layout mode="100">
                <div className={classes.content}>
                    <Text
                        component="div"
                        variant="h5"
                        color="inherit"
                        className={classes.header}
                    >
                        additional information
                    </Text>
                    <Text
                        variant="body1"
                        id={`${baseId}CareerHighlights`}
                        preserveFormat
                    >
                        {data.additionalInformation || "n/a"}
                    </Text>
                </div>
            </Layout>

            <Layout mode="100">
                <div className={classes.content}>
                    <Text
                        component="div"
                        variant="h5"
                        color="inherit"
                        className={classes.header}
                    >
                        salary and start
                    </Text>
                    <Label
                        id="desiredSalaryRange"
                        label={`${data.jobType.payLabel} range`}
                    >
                        <Text
                            variant="body1"
                            id={`${baseId}DesiredSalaryRange`}
                            className={classes.contentBodySection}
                        >
                            {!!data.desiredSalaryMaxInUSDollars ||
                            !!data.desiredSalaryMinInUSDollars
                                ? `${formattedMin} - ${formattedMax} ${data.desiredJobSalaryTypeName}`
                                : "not specified"}
                        </Text>
                    </Label>
                    <div>
                        <Label label="available to start">
                            <Text
                                variant="body1"
                                id={`${baseId}AvailableToStart`}
                            >
                                {data.noticeAvailability || "not specified"}
                            </Text>
                        </Label>
                    </div>
                </div>
            </Layout>
            <Layout mode="100">
                <Text component="div" variant="h5" color="inherit">
                    interview details
                </Text>
                <Label label="time zone">
                    <Text variant="body1" id={`${baseId}TimeZone`}>
                        {data.timeZoneName}
                    </Text>
                </Label>
                <Label label="best times to reach">
                    <div>
                        {data.bestTimesToReach.length > 0 ? (
                            data.bestTimesToReach.map(item => (
                                <Text
                                    variant="body1"
                                    key={`${item}BestTimesToReach`}
                                    id={`${baseId}BestTimeToReachItem${item
                                        .split("-")[0]
                                        .trim()}`}
                                >
                                    {item}
                                </Text>
                            ))
                        ) : (
                            <Text variant="body1">not specified</Text>
                        )}
                    </div>
                </Label>
                <Label label="interview availability notes">
                    <Text
                        variant="body1"
                        id={`${baseId}InterviewAvailability`}
                        className={classes.preserve}
                    >
                        {data.interviewAvailabilityNote}
                    </Text>
                </Label>
                <Label label="is candidate interviewing elsewhere?">
                    <Text variant="body1" id={`${baseId}InterviewingElsewhere`}>
                        {data.isInterviewingElsewhere ? "yes" : "no"}
                    </Text>
                </Label>
                {data.isInterviewingElsewhere ? (
                    <Label label="where are they in the process?">
                        <Text
                            variant="body1"
                            id={`${baseId}WhereInProcess`}
                            className={classes.preserve}
                        >
                            {data.interviewingElsewhereNote}
                        </Text>
                    </Label>
                ) : null}
            </Layout>

            {showRelocation ? (
                <Layout mode="100">
                    <Label label="is the candidate willing to relocate for this job?">
                        <Text
                            variant="body1"
                            id={`${baseId}WillingToRelocate`}
                            className={classes.preserve}
                        >
                            {data.isWillingToRelocate ? "yes" : "no"}
                        </Text>
                    </Label>
                    {data.isWillingToRelocate ? (
                        <Label label="is relocation assistance needed?">
                            <Text
                                variant="body1"
                                id={`${baseId}RelocationAssistance`}
                                className={classes.preserve}
                            >
                                {data.relocationAssistanceNeeded ? "yes" : "no"}
                            </Text>
                        </Label>
                    ) : null}
                </Layout>
            ) : null}
        </>
    );
};
